<template>
  <Headermenu :menu-color="'strategy'"
              :is-home="false"></Headermenu>
  <div class="articleContainer">
    <div class="search-container">
      <div class="searchflex">
        <p>{{ $t('home.Posts.post') }}</p>
        <div class="subtitle">
          <span class="leftArrow"></span>
          <span class="title">CUSTOM GUIDE</span>
          <span class="rightArrow"></span>
        </div>
        <div class="searchInput">
          <a-input v-model:value="searchValue"
                   :placeholder="$t('home.Posts.searchInput')"
                   enter-button="Search"
                   size="large"
                   class="inputStyle"
                   @change="searchInput" />
          <div class="IconContainer">
            <div class="iconSearch"
                 @click="onSearch"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="sector-container">
      <div class="leftSector">
        <div class="sectorContainer">
          <div class="title">{{ $t('home.Posts.allPost') }}</div>
          <div class="sectorList">
            <div v-for="item in sectorList"
                 :class="currentSector?.id == item.id ? 'sectorActive' : ''"
                 @click="selectSector(item)">{{ item.name }}</div>
          </div>
        </div>
      </div>
      <div class="rightContainerList">
        <div class="keywordsList">
          <div v-for="item in currentSector?.tag">{{ item }}</div>
        </div>
        <div class="articleTable">
          <div v-for="item in currentArticleList"
               class="container"
               @mouseover="onMouseOver(item.id)"
               @mouseout="onMouseOut"
               @click="detailArticle(item)">
            <div class="article-image">
              <a-image :src="toUrl(item.image)"
                       :preview="false"
                       class="imageStyle" />
            </div>
            <div class="articleBox">
              <div class="caption">{{ item.title }}</div>
              <div class="captionLine"></div>
              <div class="fullText">
                {{ getSimpleText(item.content) }}
              </div>
              <div class="rightArrow">
                <div class="tagLine">
                  <div v-for="itm in (item.tag as any).split(',')"
                       class="tagContent">{{ itm }}</div>
                </div>
                <div v-if="articleActive == item.id"
                     class="icon-arrow"></div>

              </div>
            </div>
          </div>
        </div>
        <div class="footerPagination">
          <APagination v-model:current="paginationCurrent"
                       v-model:pageSize="paginationSize"
                       :page-size-options=paginationSizeOptions
                       :show-size-changer="true"
                       :total="paginationTotal"
                       @change="changePagination" />
        </div>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script setup lang="ts">
import { getSimpleText, toUrl } from '@/utils'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from "vue-i18n"
import Headermenu from './components/HeaderMenu.vue'
import Footer from './components/Footer.vue'
import type { searchType, sectorType } from '../api/interface/sectorinterface'
import { APagination } from '@iota-cn/ui-components'
import { useRoute, useRouter } from 'vue-router'
import { getSectorSearch, getSectorList, getArticleFromSector } from '../api/index'
import { useHomeStore } from '../index'
import { useSeoMeta, createBlogStructuredData } from '&/composables/useSeoMeta'

const homeStoreState = useHomeStore()
const searchValue = ref<string>('')
const searchSwitch = ref<boolean>(false)
const route = useRoute()
const router = useRouter()
const sectorList = ref<sectorType[]>([])
const articleActive = ref<number>()
const currentSector = ref<sectorType>()
const currentArticleList = ref<sectorType['contents']>([])
const paginationTotal = ref(0)
const paginationSize = ref(5)
const paginationSizeOptions = ref([5, 10, 20, 50])
const paginationCurrent = ref(1)
const offset = ref<number>(0)
const tag = ref<string>('')
const isSearch = ref<boolean>(false)
const sector_id = ref()
const sectorName = ref()

onMounted(() => {
  init()
})

watch(route, () => {
  init()
})

const init = async () => {
  tag.value = route.query.tag as string
  sectorName.value = route.query.sectorName
  sector_id.value = route.params.id

  // 设置页面SEO
  setupPageSeo()

  // const isSector = !!route.query.sectorName
  if (route.query.label && route.query.label == 'search') {
    isSearch.value = true
    searchValue.value = route.query.value as string
    await onSearch()
  } else {
    // await getSectorListWithQuery(tag.value, isSector)
    getSector().then(() => {
      if (sectorList.value.length > 0) {
        if (homeStoreState.cmsContentItem.sector_id !== 0) {
          (currentSector.value as any) = {
            id: homeStoreState.cmsContentItem.sector_id,
            name: sectorName.value
          }
          paginationCurrent.value = homeStoreState.cmsContentItem.currentPage
          fetchArticleList(homeStoreState.cmsContentItem.sector_id, {
            page: homeStoreState.cmsContentItem.currentPage,
            pageSize: 5
          })
        } else {
          currentSector.value = sectorList.value[0]
          fetchArticleList(currentSector.value.id, {
            page: 1,
            pageSize: 5
          })
        }
      }
    })
  }
}

// 设置页面SEO配置
const setupPageSeo = () => {
  const { t } = useI18n()

  // 根据当前页面类型设置不同的SEO
  if (route.query.label && route.query.label === 'search') {
    // 搜索页面
    useSeoMeta({
      title: t('seo.posts.search.title', `搜索"${searchValue.value}" - 文章列表`),
      description: t('seo.posts.search.description', `搜索"${searchValue.value}"相关文章和资讯`),
      keywords: t('seo.posts.search.keywords', '搜索,文章,资讯,定制指南'),
      structuredData: createBlogStructuredData([])
    })
  } else if (currentSector.value) {
    // 具体栏目页面
    useSeoMeta({
      title: t('seo.posts.category.title', `${currentSector.value.name} - 文章列表`),
      description: t('seo.posts.category.description', `浏览${currentSector.value.name}相关文章和资讯`),
      keywords: t('seo.posts.category.keywords', `${currentSector.value.name},文章,资讯`),
      structuredData: createBlogStructuredData([])
    })
  } else {
    // 默认文章列表页面
    useSeoMeta({
      title: t('seo.posts.default.title', '定制指南与行业资讯'),
      description: t('seo.posts.default.description', '美派时MEXPRESS定制指南、行业资讯和最新动态'),
      keywords: t('seo.posts.default.keywords', '定制指南,行业资讯,最新动态,文章'),
      structuredData: createBlogStructuredData([])
    })
  }
}

const searchInput = () => {
  if (searchValue.value == '') {
    searchSwitch.value = false
  }
}

const onSearch = async () => {
  if (searchValue.value) {
    getSectorListWithQuery(searchValue.value, 5)
  } else {
    fetchArticleList((currentSector.value as sectorType).id, {
      page: 1,
      pageSize: 5
    })
  }
  searchSwitch.value = !searchSwitch.value
}

// 选择栏目查找对应文章
const selectSector = (singleSector: sectorType) => {
  currentSector.value = singleSector
  fetchArticleList(singleSector.id, {
    page: 1,
    pageSize: 5
  })
}

// 获取栏目列表
const getSector = async () => {
  await getSectorList().then(result => {
    sectorList.value = result.sectors
  })
}

// 文章详情
const detailArticle = (articleData: sectorType['contents'][number]) => {
  homeStoreState.cmsContentItem.sector_id = articleData.sector_id
  homeStoreState.cmsContentItem.currentPage = paginationCurrent.value
  router.push(`/posts/content/${articleData.id}`)
}

// 分页
const changePagination = async (page: number, pageSize: number) => {
  const { id } = currentSector.value as sectorType
  paginationCurrent.value = page
  if (searchValue.value) {
    offset.value = (page - 1) * pageSize
    getSectorListWithQuery(searchValue.value, pageSize)
  } else {
    fetchArticleList(id, { page, pageSize })
  }
}

// 根据栏目加载文章列表
const fetchArticleList = async (id: number, data: { page: number, pageSize: number }) => {
  const { page, pageSize } = data
  getArticleFromSector(id, {
    limit: pageSize,
    offset: (page - 1) * pageSize
  }).then(result => {
    currentArticleList.value = result.rows
    paginationTotal.value = result.total
  })
}

const getSectorListWithQuery = async (keywords: string, pageSize: number, isSector?: boolean,) => {
  const data: searchType = {
    keywords,
    limit: pageSize,
    offset: offset.value
  }
  isSector ? delete data.keywords : ''
  route.path !== '/posts/search' ? data.sector_id = sector_id.value : delete data.sector_id
  getSectorSearch(data).then((result: any) => {
    currentArticleList.value = result.rows
    paginationTotal.value = result.total
  })
}

const onMouseOver = (id: number) => {
  articleActive.value = id
}

const onMouseOut = () => {
  articleActive.value = 0
}

</script>

<style lang="less" scoped>
.articleContainer {
  width: 100%;
  height: 100%;
  background: #f6f6f6;
  overflow-y: auto;

  .search-container {
    display: flex;
    height: 600px;
    background: url(../assets/imgs/bg_search.png);
    background-size: contain;

    .searchflex {
      display: inline-flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 48px;
      color: #ffffff;
      width: 100%;
      height: 71%;

      p {
        margin-bottom: 0;
      }

      .subtitle {
        display: inline-flex;
        align-items: center;
        margin-bottom: 60px;

        .title {
          font-size: 14px;
          opacity: 0.3;
          margin: 0 10px 0 10px;
        }

        .leftArrow {
          background: url(../assets/imgs/search_left_arrow.png);
          background-size: contain;
          width: 164px;
          height: 7px;
        }

        .rightArrow {
          background: url(../assets/imgs/search_right_arrow.png);
          background-size: contain;
          width: 164px;
          height: 7px;
        }
      }

      .searchInput {
        display: inline-flex;
        width: 800px;
        height: 80px;

        .inputStyle {
          background: transparent;
          color: #ffffff;
          padding-left: 30px;

          &:focus,
          &:hover {
            border: 1px solid #ffffff !important;
          }
        }

        .IconContainer {
          width: 80px;
          height: 80px;
          background: #ffffff;
          display: inline-flex;
          justify-content: center;
          align-items: center;

          .iconSearch {
            background: url(../assets/imgs/ic-search.png);
            cursor: pointer;
            width: 41px;
            height: 40px;
            background-size: cover;
          }
        }
      }

    }
  }

  .sector-container {
    display: flex;
    width: 100%;
    background: #f6f6f6;
    margin-bottom: 50px;

    .leftSector {
      flex: 0.2;
      margin-top: 75px;
      display: flex;
      flex-direction: column;
      align-items: end;

      .sectorContainer {
        width: 160px;
        background: #ffffff;
        min-height: 500px;

        .title {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 80px;
          font-size: 20px;
          color: #111111;
        }

        .sectorList {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          border-top: 2px solid #f8f7f7;
          width: 100%;
          font-size: 14px;
          color: #999999;

          .sectorActive {
            color: #111111;
          }

          div {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            height: 50px;
          }
        }
      }
    }

    .rightContainerList {
      flex: 0.7;
      margin-top: 75px;

      .keywordsList {
        display: inline-flex;
        flex-direction: row;
        height: 50px;
        width: 100%;
        justify-content: flex-end;
        align-items: center;

        div {
          margin-left: 29px;
          font-size: 16px;
          color: #999999;
          line-height: 50px;

          &:hover {
            cursor: pointer;
            color: #111111;
            border-bottom: 4px solid #111111;
            border-width: 2px;
          }
        }
      }

      .articleTable {
        margin: 21px 0 21px 21px;

        .container {
          display: flex;
          align-items: center;
          background: #ffffff;
          margin-bottom: 20px;
          cursor: pointer;

          .article-image {
            flex: 0 0 200px;
            width: 264px;
            height: 198px;
            display: flex;
            justify-content: center;
            align-items: center;

            :deep(.ant-image) {
              object-fit: contain;

              .imageStyle {
                object-fit: contain;
              }
            }
          }

          .articleBox {
            height: 150px;
            margin-left: 31px;
            flex: 1;

            .caption {
              font-size: 20px;
              color: #111111;
            }

            .captionLine {
              width: 24px;
              height: 2px;
              margin: 10px 0 10px 0;
              background: #111111;
            }

            .fullText {
              width: 95%;
              font-size: 14px;
              color: #999999;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
            }

            .rightArrow {
              display: inline-flex;
              width: 91%;
              margin: 20px;
              margin-left: 0;

              .tagLine {
                width: 100%;

                .tagContent {
                  display: inline-flex;
                  color: #85a3eae8;
                }

                .tagContent:not(:first-child) {
                  margin-left: 20px;
                }
              }

              .icon-arrow {
                height: 12px;
                width: 51px;
                background: url("../assets/imgs/ic-blackArrow.png") no-repeat;
                background-size: cover !important;
              }
            }
          }
        }

        .container:hover {
          box-shadow: 0px 10px 36px 0px rgba(0, 0, 0, 0.12);
        }
      }
    }
  }

  .footerPagination {
    width: 100%;
    margin-bottom: 100px;
    display: inline-flex;
    justify-content: end;
  }

}
</style>
